"use client";

import React, { useEffect, useRef } from "react";

interface StarryBackgroundProps {
  children?: React.ReactNode;
  starsCount?: number;
  speed?: number;
  className?: string;
}

const StarryBackground: React.FC<StarryBackgroundProps> = ({
  children,
  starsCount = 200,
  speed = 50,
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas to full screen
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      // Fix for high-DPI displays
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);

      createStars(); // Recreate stars when resizing
    };

    // Create stars array
    let stars: {
      x: number;
      y: number;
      size: number;
      opacity: number;
      speed: number;
    }[] = [];

    const createStars = () => {
      stars = [];
      // Create stars with different sizes for a layered effect
      for (let i = 0; i < starsCount; i++) {
        // Create different layers of stars
        const layer = Math.random() > 0.7 ? 2 : Math.random() > 0.4 ? 1 : 0;
        const layerSizeMultiplier = [0.1, 0.5, 1][layer];
        const layerSpeedMultiplier = [0.3, 0.6, 1][layer];
        const layerOpacityMultiplier = [0.4, 0.7, 1][layer];

        stars.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: (Math.random() * 1.5 + 0.5) * layerSizeMultiplier, // Vary size by layer
          opacity: (Math.random() * 0.5 + 0.3) * layerOpacityMultiplier, // Vary opacity by layer
          speed: (Math.random() * speed * 0.01 + 0.05) * layerSpeedMultiplier, // Vary speed by layer
        });
      }
    };

    // Animation function
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Dark blue gradient background
      const gradient = ctx.createLinearGradient(0, 0, 0, window.innerHeight);
      gradient.addColorStop(0, "#0E0D15"); // Dark navy at the top
      gradient.addColorStop(1, "#0E0D15"); // Near black at the bottom

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);

      // Draw and update stars
      stars.forEach((star) => {
        // Simple stars - faster rendering
        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();

        // Occasional twinkle effect
        if (Math.random() > 0.99) {
          star.opacity = Math.min(
            1,
            star.opacity * (Math.random() * 0.5 + 1.2)
          );
        } else {
          // Return to normal opacity
          star.opacity =
            star.opacity * 0.98 + (Math.random() * 0.5 + 0.3) * 0.02;
        }

        // Move stars
        star.y -= star.speed;

        // Reset position if off screen
        if (star.y < -star.size * 2) {
          star.y = window.innerHeight + star.size * 2;
          star.x = Math.random() * window.innerWidth;
          // Randomize the size/opacity again
          star.size = Math.random() * 2 + 0.5;
          star.opacity = Math.random() * 0.8 + 0.2;
        }
      });

      requestAnimationFrame(animate);
    };

    // Handle window resize
    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();
    animate();

    // Cleanup
    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [starsCount, speed]);

  return (
    <div
      className={`relative w-full h-full overflow-hidden  z-[-100] ${className}`}
    >
      <canvas
        ref={canvasRef}
        className="fixed top-0 left-0 w-screen h-screen"
      />
      <div className="relative z-[-1]">{children}</div>
    </div>
  );
};

export default StarryBackground;
